// src/components/dashboards/NLResultCard.tsx
// The presentation of a single NL-query result: question pill + CSV, explanation, cache strip,
// metadata chips, truncation alert, per-patient export, the agentic chart, the clickable patient
// sidebar, and the data table. Extracted verbatim from DashboardChat so the SAME presentation can be
// reused inside each ambiguous interpretation section (see DisambiguationPanel) without duplicating
// styling — same colors, same white-on-navy table header, same patient links. The flags let the
// disambiguation context hide the parts that only make sense for a single, committed result
// (question pill, cache strip, follow-ups). Joan, 2026-06-23.

import React, { useMemo } from "react";
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TableChartIcon from "@mui/icons-material/TableChart";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AgenticChartRenderer from "./charts/agentic/AgenticChartRenderer";
import { downloadNlCsvExportScoped } from "./nlqCsv";
import { useSnackbar } from "notistack";
import { NLQueryResult } from "hooks/useNLQuery";
import { useTranslation } from "../../i18n";
import { useDashboardInk } from "./dashboardInk";
import { athenaColors } from "../../theme/palette";
import { glassContentSx, glassTableSx } from "../../theme/glassTokens";

const MAX_DISPLAY_ROWS = 50;
const MAX_DISPLAY_COLS = 12;

export function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toFixed(2);
  }
  return String(value);
}

interface NLResultCardProps {
  result: NLQueryResult;
  /** Env filter of the on-screen query, so the export-scoped CSV re-runs the same scope. */
  environmentIds?: number[];
  hideInputBar?: boolean;
  onExportSelected?: (ids: number[]) => void;
  onFollowup?: (suggestion: string) => void;
  onClearCache?: (rerunQuestion: string) => void;
  /** Hide parts that only make sense for a single committed result (used by the option context). */
  showQuestionPill?: boolean;
  showExplanation?: boolean;
  showCachedStrip?: boolean;
  showExportButton?: boolean;
  showFollowups?: boolean;
}

const NLResultCard: React.FC<NLResultCardProps> = ({
  result,
  environmentIds,
  hideInputBar,
  onExportSelected,
  onFollowup,
  onClearCache,
  showQuestionPill = true,
  showExplanation = true,
  showCachedStrip = true,
  showExportButton = true,
  showFollowups = true,
}) => {
  const theme = useTheme();
  const ink = useDashboardInk();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Export-scoped CSV download (066 B / 3.1): re-run the query server-side restricted to the
  // user's export scope, warn when patients are excluded, then save. Viewing stays on the
  // view scope; only the DOWNLOAD is narrowed to what the user may export.
  const handleDownloadCsv = async () => {
    try {
      const { excludedCount, includedCount, hadRows } = await downloadNlCsvExportScoped({
        question: result.question,
        sessionId: result.session_id,
        environmentIds,
      });
      if (includedCount === 0) {
        enqueueSnackbar(
          t(
            "nlq.csv.none_exportable",
            "Nothing to download: none of these patients are in your export scope."
          ),
          { variant: "warning" }
        );
      } else if (!hadRows) {
        // Export scope is non-empty but the query matched no rows within it → nothing saved.
        enqueueSnackbar(
          t("nlq.csv.no_rows", "No rows in your export scope matched this query."),
          { variant: "warning" }
        );
      } else if (excludedCount > 0) {
        enqueueSnackbar(
          t(
            "nlq.csv.some_excluded",
            "Downloaded your exportable patients only; {n} outside your export scope were excluded."
          ).replace("{n}", String(excludedCount)),
          { variant: "info" }
        );
      }
    } catch {
      enqueueSnackbar(
        t("nlq.csv.failed", "Could not download the CSV."),
        { variant: "error" }
      );
    }
  };

  const patientEntries = useMemo(() => {
    if (!result || !result.rows || result.rows.length === 0) return [];
    const idCol =
      result.columns.find((c) => c === "patient_id") ||
      result.columns.find((c) => c === "id") ||
      null;
    const nameCol =
      result.columns.find((c) => c === "patient_name") ||
      result.columns.find((c) => c === "name") ||
      result.columns.find((c) => c === "full_name") ||
      null;
    if (!idCol) return [];
    const seen = new Set<number>();
    const entries: { id: number; name: string }[] = [];
    for (const row of result.rows) {
      const rawId = row[idCol];
      const numId = typeof rawId === "number" ? rawId : parseInt(String(rawId), 10);
      if (isNaN(numId) || seen.has(numId)) continue;
      seen.add(numId);
      const name = nameCol ? String(row[nameCol] ?? `Patient ${numId}`) : `Patient ${numId}`;
      entries.push({ id: numId, name });
    }
    return entries;
  }, [result]);

  const patientIds = useMemo(() => patientEntries.map((e) => e.id), [patientEntries]);

  return (
    // Ink lives HERE, not at the call sites. DashboardChat renders this inside
    // cardSx (which carries the same block), but DisambiguationPanel renders it
    // in its own chrome, and that path shipped with an unreadable table: the
    // rows were still deepNavy on the dark card. Owning it here means any
    // future consumer is inked by construction.
    <Box sx={ink.dark ? { ...glassContentSx, ...glassTableSx } : undefined}>
      {/* Header: question pill + CSV, explanation, cache strip, chips, truncation, export. */}
      <Box
        sx={{
          px: hideInputBar ? { xs: 2.5, sm: 5 } : 2,
          py: hideInputBar ? { xs: 2, sm: 2.5 } : 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        {showQuestionPill && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.5,
                borderRadius: "12px",
                background: `linear-gradient(180deg, ${alpha(
                  theme.palette.primary.dark,
                  0.95,
                )} 0%, ${alpha(theme.palette.primary.dark, 0.85)} 100%)`,
                color: theme.palette.common.white,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 14, color: theme.palette.secondary.main, flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  letterSpacing: 0.1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {result.question}
              </Typography>
            </Box>
            {result.rows && result.rows.length > 0 && (
              <Tooltip title={t("dashboard_chat.download_csv", "Download result (CSV)")} arrow>
                <Button
                  size="small"
                  startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                  onClick={handleDownloadCsv}
                  sx={{
                    flexShrink: 0,
                    textTransform: "none",
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    px: 1.25,
                    minHeight: 24,
                    height: 24,
                    bgcolor: alpha(
                      ink.dark ? theme.palette.common.white : theme.palette.info.main,
                      0.1,
                    ),
                    color: ink.dark
                      ? theme.palette.common.white
                      : theme.palette.info.main,
                    borderRadius: "12px",
                    border: "1px solid transparent",
                    backdropFilter: "blur(8px)",
                    transition: "all 0.25s ease",
                    // glassContentSx dims every icon inside the card to 50%
                    // white, which on the card's one primary action read as
                    // disabled. Re-asserted here, above the container rule.
                    "& .MuiSvgIcon-root": { color: "inherit" },
                    "&:hover": {
                      bgcolor: alpha(
                        ink.dark
                          ? athenaColors.periwinkle
                          : theme.palette.primary.main,
                        0.18,
                      ),
                      color: ink.dark
                        ? theme.palette.common.white
                        : theme.palette.primary.main,
                      borderColor: alpha(
                        ink.dark
                          ? athenaColors.periwinkle
                          : theme.palette.primary.main,
                        0.4,
                      ),
                    },
                  }}
                >
                  CSV
                </Button>
              </Tooltip>
            )}
          </Box>
        )}

        {showExplanation && result.explanation && (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              color: theme.custom?.contentText?.primary || theme.palette.text.primary,
              fontWeight: 400,
              lineHeight: 1.4,
            }}
          >
            {result.explanation}
          </Typography>
        )}

        {showCachedStrip && result.cached && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mt: 1,
              px: 1,
              py: 0.4,
              borderRadius: "10px",
              bgcolor: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              color: theme.palette.info.main,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 13 }} />
            <Typography variant="caption" sx={{ fontSize: "0.7rem", lineHeight: 1.4, fontWeight: 500 }}>
              {t("dashboard_chat.cached_result", "Cached result")}
              {result.cached_at && <> · {new Date(result.cached_at).toLocaleString()}</>}
              {onClearCache && (
                <>
                  {" · "}
                  <Box
                    component="span"
                    onClick={() => onClearCache(result.question)}
                    sx={{
                      cursor: "pointer",
                      fontWeight: 600,
                      // A Box, not a Typography, so glassContentSx's
                      // descendant rules never reach it — and primary.main is
                      // deepNavy, so on the dark strip the action disappeared
                      // entirely.
                      color: ink.dark
                        ? athenaColors.periwinkle
                        : theme.palette.primary.main,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {t("dashboard_chat.refresh_query", "Refresh")}
                  </Box>
                </>
              )}
            </Typography>
          </Box>
        )}

        {/* Chips: intent + visualization + tables used + row count. */}
        <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap", alignItems: "center" }}>
          {(() => {
            const pillSx = {
              height: 24,
              fontSize: "0.72rem",
              fontWeight: 500,
              color: theme.palette.info.main,
              bgcolor: alpha(theme.palette.info.main, 0.1),
              border: "1px solid transparent",
              backdropFilter: "blur(8px)",
              "& .MuiChip-icon": { color: theme.palette.info.main, fontSize: 13 },
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.18),
                color: theme.palette.primary.main,
                "& .MuiChip-icon": { color: theme.palette.primary.main },
              },
            } as const;
            return (
              <>
                {result.intent && (
                  <Chip icon={<AutoAwesomeIcon />} label={result.intent.intent} size="small" sx={pillSx} />
                )}
                {result.visualization && result.visualization.type !== "table" && (
                  <Chip label={result.visualization.type.replace("_", " ")} size="small" sx={pillSx} />
                )}
                {result.tables_used.map((tbl) => (
                  <Chip
                    key={tbl}
                    icon={<TableChartIcon />}
                    label={tbl.replace("clinical_", "")}
                    size="small"
                    sx={pillSx}
                  />
                ))}
                <Chip label={`${result.row_count} rows`} size="small" sx={pillSx} />
              </>
            );
          })()}
        </Box>

        {result.truncated && (
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{
              mt: 1.5,
              py: 0,
              px: 1,
              fontSize: "0.72rem",
              alignItems: "center",
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
              "& .MuiAlert-message": { py: 0.5, fontSize: "0.72rem", lineHeight: 1.3 },
              "& .MuiAlert-icon": { py: 0.5, mr: 0.75 },
            }}
          >
            {t(
              "dashboard_chat.results_limited",
              "Results are limited to {max} rows for performance.",
            ).replace("{max}", String(result.max_rows ?? 500))}
          </Alert>
        )}

        {showExportButton &&
          result.rows &&
          result.rows.length > 0 &&
          patientIds.length > 0 &&
          onExportSelected && (
            <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                onClick={() => onExportSelected(patientIds)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  borderColor: alpha(theme.palette.divider, 0.3),
                  color: theme.custom?.contentText?.secondary || theme.palette.text.secondary,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {t("dashboard_chat.export_patients_full", "Export {count} patient{plural} (full data)")
                  .replace("{count}", String(patientIds.length))
                  .replace("{plural}", patientIds.length !== 1 ? "s" : "")}
              </Button>
            </Box>
          )}
      </Box>

      {/* Agentic chart visualization */}
      {result.visualization && result.visualization.type !== "table" && (
        <Box
          sx={{
            px: hideInputBar ? { xs: 2.5, sm: 5 } : 2,
            py: hideInputBar ? { xs: 2, sm: 2.5 } : 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <AgenticChartRenderer result={result} />
        </Box>
      )}

      {/* Patient links sidebar (left) + data table (right) */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          ...(hideInputBar && { px: { xs: 2.5, sm: 5 }, pb: { xs: 2, sm: 2.5 } }),
        }}
      >
        {patientEntries.length > 0 && (
          <Box
            sx={{
              width: { xs: "100%", md: 220 },
              minWidth: { md: 220 },
              maxHeight: 400,
              overflowY: "auto",
              borderRight: { md: `1px solid ${alpha(theme.palette.divider, 0.22)}` },
              borderBottom: {
                xs: `1px solid ${alpha(theme.palette.divider, 0.22)}`,
                md: "none",
              },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none" },
              p: 1.5,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{
                display: "block",
                mb: 1,
                color: theme.palette.primary.main,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                fontSize: "0.65rem",
              }}
            >
              {t("dashboard_chat.patients_sidebar", "Patients ({count})").replace(
                "{count}",
                String(patientEntries.length),
              )}
            </Typography>
            {patientEntries.map((entry) => (
              <Link
                key={entry.id}
                href={`/patients/${entry.id}/details`}
                underline="none"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: "0.78rem",
                  fontWeight: 500,
                  color: theme.palette.primary.main,
                  transition: "all 0.15s ease",
                  "&:hover": {
                    background: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.dark,
                  },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                {entry.name}
              </Link>
            ))}
          </Box>
        )}

        {result.rows.length > 0 && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TableContainer
              sx={{
                maxHeight: { xs: 380, md: "calc(100vh - 360px)" },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {result.columns.slice(0, MAX_DISPLAY_COLS).map((col) => (
                      <TableCell
                        key={col}
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          letterSpacing: 0.3,
                          whiteSpace: "nowrap",
                          color: theme.palette.common.white,
                          background: `linear-gradient(180deg, ${alpha(
                            theme.palette.primary.dark,
                            0.92,
                          )} 0%, ${alpha(theme.palette.primary.dark, 0.82)} 100%)`,
                          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
                          borderRight: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                          "&:last-of-type": { borderRight: "none" },
                        }}
                      >
                        {col.replace(/_/g, " ")}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.rows.slice(0, MAX_DISPLAY_ROWS).map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        "&:nth-of-type(odd)": { background: alpha(theme.palette.common.white, 0.02) },
                        "&:nth-of-type(even)": { background: alpha(theme.palette.secondary.main, 0.04) },
                        "&:hover": { background: alpha(theme.palette.primary.main, 0.06) },
                      }}
                    >
                      {result.columns.slice(0, MAX_DISPLAY_COLS).map((col) => (
                        <TableCell
                          key={col}
                          sx={{
                            fontSize: "0.72rem",
                            whiteSpace: "nowrap",
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
                            borderRight: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                            "&:last-of-type": { borderRight: "none" },
                          }}
                        >
                          {formatCell(row[col])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {result.row_count > MAX_DISPLAY_ROWS && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", p: 1, textAlign: "center" }}
              >
                {t("dashboard_chat.showing_rows", "Showing {displayed} of {total} rows")
                  .replace("{displayed}", String(MAX_DISPLAY_ROWS))
                  .replace("{total}", String(result.row_count))}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Follow-up chips */}
      {showFollowups && !hideInputBar && result.suggested_followups && result.suggested_followups.length > 0 && (
        <Box
          sx={{
            p: 1.5,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: "flex",
            gap: 0.75,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.custom?.contentText?.secondary || theme.palette.text.secondary,
              fontSize: "0.65rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              mr: 0.5,
            }}
          >
            {t("dashboard_chat.follow_up", "Follow up")}
          </Typography>
          {result.suggested_followups.map((suggestion) => (
            <Chip
              key={suggestion}
              label={suggestion}
              size="small"
              clickable
              onClick={() => onFollowup && onFollowup(suggestion)}
              sx={{
                height: 24,
                fontSize: "0.7rem",
                fontWeight: 500,
                borderColor: alpha(theme.palette.primary.main, 0.25),
                color: theme.palette.primary.main,
                "&:hover": {
                  background: alpha(theme.palette.primary.main, 0.08),
                  borderColor: theme.palette.primary.main,
                },
              }}
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default NLResultCard;
